import type React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useMemoizedFn } from "ahooks";
import { OspToast } from "@ospweb/sdk-ui";
import log from "logbeacon";
import type { SenderAttachment } from "./type";
import { hasTransferFiles } from "./sender.attachmentUtils";
import {
  SENDER_IMAGE_ATTACHMENT_RULE,
  SENDER_DOCUMENT_ATTACHMENT_RULE,
  getSenderAttachmentFileKind,
  isSenderAttachmentFileOversized,
  SENDER_MAX_FILES,
  SENDER_MAX_FILE_SIZE_MB,
  SENDER_MAX_IMAGE_SIZE_MB,
} from "./sender.fileAttachmentRules";
import { I18nUtil } from "@utils/i18n";
import { uploadAttachmentFiles } from "@/src/utils/attachmentUpload";
import Translations from "@translate/components/Sender/sender.fileAttachments";
import {
  clickHiddenFileInput,
  FILE_PICK_PRESETS,
  type PrepareFileInputParams,
} from "@/src/lib/bridge/prepareFileInput";
import { getBridge } from "@/src/lib/bridge/bridge";

const I18n = I18nUtil.createScoped(Translations);

export type UseSenderFileAttachmentsParams = {
  disabled: boolean;
  loading: boolean;
  isLoggedIn: boolean;
  hasFileUploadAccess: boolean;
  authHeaders: Record<string, string>;
  fingerprint?: string | number | null;
  openLoginModal?: () => void;
  /** Called after a successful drop handling (e.g. clear drag highlight) */
  onDragStateClear?: () => void;
};

export function useSenderFileAttachments({
  disabled,
  loading,
  isLoggedIn,
  hasFileUploadAccess,
  authHeaders,
  fingerprint,
  openLoginModal,
  onDragStateClear,
}: UseSenderFileAttachmentsParams) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<SenderAttachment[]>([]);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const isUploading = attachments.some((att) => att.status === "uploading");

  const readyAttachments = useMemo(
    () =>
      attachments
        .filter((att) => att.status !== "uploading" && att.status !== "error")
        .map(({ type, filename, mediaType, url, size }) => ({
          type,
          filename,
          mediaType,
          url,
          size,
        })),
    [attachments],
  );

  const ensureCanPickFiles = useMemoizedFn((): boolean => {
    if (disabled || loading) {
      return false;
    }
    if (!isLoggedIn) {
      openLoginModal?.();
      return false;
    }
    if (!hasFileUploadAccess) {
      return false;
    }
    return true;
  });

  const clickFileInput = useMemoizedFn(
    async (
      inputRef: React.RefObject<HTMLInputElement | null>,
      preset?: PrepareFileInputParams,
    ) => {
      if (!ensureCanPickFiles()) {
        return;
      }
      if (preset) {
        await clickHiddenFileInput(getBridge(), inputRef, preset);
        return;
      }
      inputRef.current?.click();
    },
  );

  /** Desktop：合并 accept 的 file picker（不经过 prepareFileInput） */
  const handlePickFiles = useMemoizedFn(() => {
    void clickFileInput(fileInputRef);
  });

  const handlePickPhoto = useMemoizedFn(() => {
    void clickFileInput(photoInputRef, FILE_PICK_PRESETS.photo);
  });

  const handlePickCamera = useMemoizedFn(() => {
    void clickFileInput(cameraInputRef, FILE_PICK_PRESETS.camera);
  });

  const handlePickDocuments = useMemoizedFn(() => {
    void clickFileInput(documentInputRef, FILE_PICK_PRESETS.files);
  });

  const handleRemoveAttachment = useMemoizedFn((index: number) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== index));
  });

  const handleFilesSelected = useMemoizedFn(async (selected: File[]) => {
    if (disabled || loading || !selected.length) {
      return;
    }
    if (!isLoggedIn) {
      openLoginModal?.();
      return;
    }
    if (!hasFileUploadAccess) {
      return;
    }

    let nextSelected = selected;

    const unsupported = nextSelected.filter(
      (f) => getSenderAttachmentFileKind(f.name) === "unsupported",
    );
    if (unsupported.length > 0) {
      OspToast.error({
        content: I18n.t("Format not supported. %{var0}", {
          var0: `Images: ${SENDER_IMAGE_ATTACHMENT_RULE.displayFormats}. Files: ${SENDER_DOCUMENT_ATTACHMENT_RULE.displayFormats}.`,
        }),
      });
      nextSelected = nextSelected.filter(
        (f) => getSenderAttachmentFileKind(f.name) !== "unsupported",
      );
      if (nextSelected.length === 0) {
        return;
      }
    }

    const oversized = nextSelected.filter(isSenderAttachmentFileOversized);
    if (oversized.length > 0) {
      OspToast.error({
        content: I18n.t("File too large (image ≤ %{var0} / file ≤ %{var1})", {
          var0: `${SENDER_MAX_IMAGE_SIZE_MB}MB`,
          var1: `${SENDER_MAX_FILE_SIZE_MB}MB`,
        }),
      });
      nextSelected = nextSelected.filter(
        (f) => !isSenderAttachmentFileOversized(f),
      );
      if (nextSelected.length === 0) {
        return;
      }
    }

    const remaining = SENDER_MAX_FILES - attachments.length;
    if (remaining <= 0) {
      OspToast.error({
        content: I18n.t(
          "Some of your files could not be uploaded because you can only upload %{var0} attachments at a time",
          { var0: SENDER_MAX_FILES },
        ),
      });
      return;
    }
    if (nextSelected.length > remaining) {
      OspToast.warning({
        content: I18n.t(
          "Some of your files could not be uploaded because you can only upload %{var0} attachments at a time",
          { var0: SENDER_MAX_FILES },
        ),
      });
      nextSelected = nextSelected.slice(0, remaining);
    }

    const batchId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const placeholders: SenderAttachment[] = nextSelected.map((file) => ({
      type: "file" as const,
      filename: file.name,
      mediaType: file.type || undefined,
      size: file.size,
      url: "",
      status: "uploading" as const,
      _batchId: batchId,
    }));
    setAttachments((prev) => [...prev, ...placeholders]);

    try {
      const uploaded = await uploadAttachmentFiles({
        files: nextSelected,
        authHeaders,
        fingerprint,
      });

      setAttachments((prev) => {
        const next = [...prev];
        let uploadIdx = 0;
        for (let i = 0; i < next.length && uploadIdx < uploaded.length; i++) {
          if (next[i]._batchId === batchId && next[i].status === "uploading") {
            const item = uploaded[uploadIdx++];
            next[i] = {
              ...next[i],
              filename: item.filename,
              mediaType: item.mediaType || next[i].mediaType,
              size: item.size,
              url: item.url,
              status: "done",
              _batchId: undefined,
            };
          }
        }
        return next;
      });
    } catch (err) {
      log.error("[Sender] file upload error", err);
      setAttachments((prev) =>
        prev.map((att) =>
          att._batchId === batchId && att.status === "uploading"
            ? { ...att, status: "error" as const, _batchId: undefined }
            : att,
        ),
      );
    }
  });

  const handleDropToUpload = useMemoizedFn(
    async (e: React.DragEvent<HTMLDivElement>) => {
      if (disabled || loading) {
        return;
      }
      if (!hasTransferFiles(e.dataTransfer)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onDragStateClear?.();
      const selected = Array.from(e.dataTransfer.files || []);
      await handleFilesSelected(selected);
    },
  );

  const makeFileInputChangeHandler = useMemoizedFn(
    (inputRef: React.RefObject<HTMLInputElement | null>) =>
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(e.target.files || []);
        try {
          await handleFilesSelected(picked);
        } finally {
          if (inputRef.current) {
            inputRef.current.value = "";
          }
        }
      },
  );

  const handleFileChange = useMemoizedFn(
    makeFileInputChangeHandler(fileInputRef),
  );

  const handlePhotoChange = useMemoizedFn(
    makeFileInputChangeHandler(photoInputRef),
  );

  const handleCameraChange = useMemoizedFn(
    makeFileInputChangeHandler(cameraInputRef),
  );

  const handleDocumentChange = useMemoizedFn(
    makeFileInputChangeHandler(documentInputRef),
  );

  const handlePaste = useMemoizedFn(
    async (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (disabled || loading) {
        return;
      }
      const files = Array.from(e.clipboardData.files || []);
      if (files.length === 0) {
        return;
      }
      e.preventDefault();
      await handleFilesSelected(files);
    },
  );

  return {
    attachments,
    setAttachments,
    readyAttachments,
    isUploading,
    fileInputRef,
    photoInputRef,
    cameraInputRef,
    documentInputRef,
    clearAttachments,
    handlePickFiles,
    handlePickPhoto,
    handlePickCamera,
    handlePickDocuments,
    handleRemoveAttachment,
    handleFilesSelected,
    handleFileChange,
    handlePhotoChange,
    handleCameraChange,
    handleDocumentChange,
    handleDropToUpload,
    handlePaste,
  };
}
